/* iter-45 — Admin demo-funnel dashboard widget.
 * Shows demo sessions, lock-click telemetry, and top-locked features
 * for the last 7 days. Read-only — no actions to take here, just
 * insight into which CTAs are driving the upgrade modal.
 */
import React, { useEffect, useState } from 'react';
import { PlayCircle, Lock, Users, TrendingUp, RotateCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { demoApi } from '@/lib/auth_api';

export default function DemoFunnelPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setBusy(true);
    try {
      setData(await demoApi.metrics());
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load demo metrics.');
    } finally { setBusy(false); }
  };

  useEffect(() => { reload(); }, []);

  if (data === null) {
    return (
      <div className="flex items-center gap-2 p-6 text-[12px] text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading demo funnel…
      </div>
    );
  }

  const conversionPct = data.engaged_users_week > 0
    ? Math.round((data.high_intent_week / data.engaged_users_week) * 100)
    : 0;
  const max = Math.max(1, ...data.top_features.map(f => f.count));

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold tracking-tight">Demo Funnel</h2>
          <p className="text-[11.5px] text-muted-foreground">
            Sessions, lock-click telemetry, and which gated features drive the most upgrade modals.
          </p>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={reload} disabled={busy} data-testid="demo-metrics-refresh">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />} Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="demo-metrics-cards">
        <Stat icon={PlayCircle} label="Demo sessions · 7d"  value={data.sessions_week}  sub={`${data.sessions_total} all-time`} />
        <Stat icon={Lock}       label="Lock clicks · 7d"     value={data.locks_week}      sub={`${data.locks_total} all-time`} />
        <Stat icon={Users}      label="Engaged users · 7d"   value={data.engaged_users_week} sub="hit ≥1 lock" />
        <Stat icon={TrendingUp} label="High-intent · 7d"     value={data.high_intent_week}
              sub={`${conversionPct}% of engaged · saw upgrade modal`} accent />
      </div>

      <div className="rounded-md border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
            Top locked features (last 7 days)
          </div>
          <div className="text-[10.5px] text-muted-foreground">
            Updated {new Date(data.generated_at).toLocaleTimeString()}
          </div>
        </div>
        {data.top_features.length === 0 ? (
          <div className="py-6 text-center text-[12px] text-muted-foreground">
            No lock-clicks yet this week. Once visitors start hitting locks, you&apos;ll see the leaderboard here.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="demo-metrics-top-features">
            {data.top_features.map((f) => (
              <li key={f.feature} className="flex items-center gap-3">
                <span className="w-40 shrink-0 truncate text-[12.5px]">{f.feature}</span>
                <div className="relative flex-1 overflow-hidden rounded-sm bg-secondary/40">
                  <div
                    className="h-5 rounded-sm bg-primary/60"
                    style={{ width: `${Math.max(6, (f.count / max) * 100)}%` }}
                  />
                </div>
                <span className="w-10 shrink-0 text-right font-mono-stat text-[11.5px] text-foreground">{f.count}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground">
        High-intent users = those who hit the 3-lock threshold (and saw the upgrade modal). Conversion to a paid
        account isn&apos;t tracked yet — the upgrade flow drops them into Google sign-in, so any new user with an email
        that hadn&apos;t been seen before is effectively a candidate.
      </p>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, accent = false }) {
  return (
    <div className={`rounded-md border border-border bg-card p-3 ${accent ? 'border-primary/40 bg-primary/5' : ''}`}>
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-1 font-mono-stat text-2xl font-semibold tracking-tight">{value.toLocaleString()}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
